import { useMemo, useRef, useState } from "react"
import { isValidDate } from "@/utils/format-date"
import { Calendar, Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { Clock, X } from "lucide-react"

import { TimeSelect } from "../../time-picker/time-select"
import { BaseInlineInput } from "./base"
import { InlineDatePickerProps } from "./props"
import { useInlineEdit } from "./use-inline-edit"

export function InlineDatePicker({
  ariaLabel,
  label,
  LabelIcon,
  hideLabel = false,
  size,
  placeholder = "Select date",
  value: controlledValue,
  defaultValue,
  onChange,
  onSave,
  disabled = false,
  clearable = true,
  mode = "single",
  fromDate,
  toDate,
  dateFormat = "PP",
  fixedLabelWidth,
  includeTime = false,
  showTime: controlledShowTime,
  onShowTimeChange,
  onTimeChange,
}: InlineDatePickerProps) {
  const triggerRef = useRef<HTMLDivElement>(null)

  const { value, setValue, setValueSilently } = useInlineEdit({
    value: controlledValue,
    defaultValue,
    onChange,
    onSubmit: onSave,
    ref: triggerRef,
    disableClickOutside: true,
    submitOnChange: true,
    isEmpty: (val) => val === null || val === undefined,
  })

  const [isOpen, setIsOpen] = useState(false)
  const [internalShowTime, setInternalShowTime] = useState(controlledShowTime ?? false)
  const showTime = controlledShowTime ?? internalShowTime
  const setShowTime = onShowTimeChange ?? setInternalShowTime
  const [localTimeValue, setLocalTimeValue] = useState<string | null>(null)

  function handleSelect(date: Date | undefined) {
    if (!date) {
      setValue(null)
      setIsOpen(false)
      return
    }
    if (value && showTime && isValidDate(value)) {
      date.setHours(value.getHours(), value.getMinutes())
    }
    setValue(date)
    setIsOpen(false)
  }

  function handleClear() {
    setValue(null)
    if (showTime) setShowTime(false)
    setIsOpen(false)
  }

  function applyTimeChange(newDate: Date, nextShowTime: boolean) {
    // When a combined handler is provided, persist the date and all-day flag in a single
    // write so the two updates can't race and null out the deadline. Sync the local display
    // value silently (without setValue's submitOnChange firing the separate onSave write we're
    // avoiding) so the picker reflects the change immediately — including when the mutation's
    // optimistic update targets a different query key than the one feeding this view (e.g. a
    // task slideover opened from a notification, where taskId != task.id).
    if (onTimeChange) {
      setValueSilently(newDate)
      onTimeChange(newDate, nextShowTime)
      return
    }
    setValue(newDate)
    if (showTime !== nextShowTime) setShowTime(nextShowTime)
  }

  function handleTimeChange(timeString: string) {
    if (!value || !isValidDate(value)) return
    const d = value
    if (timeString === "none") {
      const newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      setLocalTimeValue("none")
      applyTimeChange(newDate, false)
      return
    }
    const [hours, minutes] = timeString.split(":")
    const newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), parseInt(hours), parseInt(minutes), 0, 0)
    setLocalTimeValue(timeString)
    applyTimeChange(newDate, true)
  }

  function getTimeValue(date: Date | null | undefined): string {
    if (localTimeValue !== null) return localTimeValue
    if (!date || !showTime) return "none"
    const h = date.getHours()
    const m = date.getMinutes()
    if (h === 0 && m === 0) return "none"
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  const displayFormat = showTime ? "PP '@' h:mm a" : dateFormat
  const displayValue = value && isValidDate(value) ? format(value, displayFormat) : null
  const calendarDisabled = useMemo(() => {
    if (fromDate && toDate) return { before: fromDate, after: toDate }
    if (fromDate) return { before: fromDate }
    if (toDate) return { after: toDate }
    return undefined
  }, [fromDate, toDate])

  return (
    <BaseInlineInput
      label={label}
      LabelIcon={LabelIcon}
      hideLabel={hideLabel}
      size={size}
      fixedLabelWidth={fixedLabelWidth}
    >
      <div className="flex w-full items-center">
        <Popover
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open)
            if (!open) setLocalTimeValue(null)
          }}
          modal
        >
          <PopoverTrigger asChild>
            <div ref={triggerRef} className="w-full">
              <button
                type="button"
                aria-label={ariaLabel}
                className={cn(
                  "focus:outline-primary flex h-8 w-full items-center justify-between rounded-md border-1 border-transparent px-2 shadow-none focus:outline-1 md:text-sm",
                  !value && "text-input-placeholder",
                  disabled ? "cursor-default opacity-50" : "hover:bg-muted cursor-pointer"
                )}
                disabled={disabled}
              >
                <span className={cn("truncate", !value && "text-input-placeholder")}>
                  {displayValue ?? placeholder}
                </span>
              </button>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode={mode as "single"}
              selected={value && isValidDate(value) ? value : undefined}
              onSelect={handleSelect}
              startMonth={fromDate}
              endMonth={toDate}
              disabled={calendarDisabled}
              initialFocus
            />
            {includeTime && (
              <div className="border-border border-t px-3 py-2">
                <div className="flex h-7 items-center gap-2">
                  <Clock className="h-4 w-4 opacity-70" />
                  <TimeSelect
                    value={getTimeValue(value)}
                    onChange={handleTimeChange}
                    disabled={disabled}
                    size="xs"
                    includeNone
                  />
                  {clearable && value && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="text-muted-foreground hover:bg-muted ml-auto flex items-center gap-1.5 rounded-sm px-2 py-1 text-sm"
                    >
                      <X className="size-3.5" />
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
            {clearable && value && !includeTime && (
              <div className="p-2">
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-muted-foreground hover:bg-muted flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
                >
                  <X className="size-4" />
                  Clear date
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </BaseInlineInput>
  )
}
